import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { api } from '../services/api';

interface User {
  id: number;
  sub: string;
  cnpj: string;
  email: string;
  phone: string;
  profile: string;
  name: string;
  documents: {
    id: number;
    document: string;
    created_at: string;
    updated_at: string;
    deleted_at: string | null;
  }[];
}

interface AuthData {
  email: string;
  password: string;
}

interface AuthContextData {
  user: User;
  signIn(credentials: AuthData): Promise<any>;
  forgotPassword(email: string): Promise<any>;
  changePassword(email: string, password: string, code: string): Promise<any>;
  signOut(): void;
}

interface AppProps {
  children: React.ReactNode;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

export const AuthProvider = ({ children }: AppProps) => {
  const [user, setUser] = useState<User>({} as User);
  const [token, setToken] = useState('');

  const signIn = useCallback(async ({ email, password }: AuthData) => {
    try {
      localStorage.clear();
      const response = await api.post('/users/login', { email, password });
      const { token: tokenResponse, user: userResponse } = response.data;

      localStorage.setItem('@Zignet:token', tokenResponse);

      setToken(tokenResponse);
      setUser(userResponse);

      api.defaults.headers.common.authorization = `Bearer ${tokenResponse}`;

      const authHeader = String(api.defaults.headers.common.authorization);
      console.log(
        '🚀 ~ file: auth.tsx ~ line 141 ~ useEffect ~ authHeader',
        authHeader
      );

      return {
        status: 'success',
        message: 'success',
        data: response.data,
      };
    } catch (error: any) {
      return {
        status: 'error',
        message: error.response.data.message,
      };
    }
  }, []);

  const forgotPassword = useCallback(async (email: string) => {
    try {
      const response = await api.post('/users/forgot-password', { email });

      return {
        status: 'success',
        message: 'success',
        data: response.data,
      };
    } catch (error: any) {
      return {
        status: 'error',
        message: error.response.data.message,
      };
    }
  }, []);

  const changePassword = useCallback(
    async (email: string, password: string, code: string) => {
      try {
        const response = await api.post('/users/confirm-password', {
          email,
          password,
          codeConfirmation: code,
        });

        return {
          status: 'success',
          message: 'success',
          data: response.data,
        };
      } catch (error: any) {
        return {
          status: 'error',
          message: error.response.data.message,
        };
      }
    },
    []
  );

  const signOut = useCallback(() => {
    localStorage.clear();

    setUser({} as User);
  }, []);

  const values = useMemo(
    () => ({ user, signIn, signOut, forgotPassword, changePassword }),
    [user, signIn, signOut, forgotPassword, changePassword]
  );

  useEffect(() => {
    const tokenStorage = localStorage.getItem('@Zignet:token');
    const authHeader = String(api.defaults.headers.common.authorization);

    if (tokenStorage && !(authHeader.length > 10)) {
      setToken(tokenStorage);

      api.defaults.headers.common.authorization = `Bearer ${tokenStorage}`;

      api.get<User>('/users/me').then((response) => setUser(response.data));
    }
  }, []);

  return <AuthContext.Provider value={values}>{children}</AuthContext.Provider>;
};

export function useAuth(): AuthContextData {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}
